import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Bell, FileSignature } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ESignatureModal, type ESignaturePayload } from '@/components/esignature/ESignatureModal';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

// TODO(signals): charts for disproportionality time-series deferred for MVP.

interface SignalAction {
  id: string;
  // SignalAction columns from prisma: actionType, performedAt, performedBy, rationale.
  actionType: string;
  rationale: string | null;
  performedAt: string;
  performedBy: string | null;
}

// Field shape matches the Signal Prisma model — see apps/api/prisma/schema.prisma.
interface SignalDetail {
  id: string;
  activeSubstance: string | null;
  meddraPreferredTerm: string | null;
  meddraSoc: string | null;
  detectionSource: string | null;
  validationStatus: string | null;
  priorityLevel: string | null;
  disposition: string | null;
  caseCount: number | null;
  notificationType: string | null;
  dayZero: string | null;
  notificationClockDue: string | null;
  notificationSentAt: string | null;
  // Persisted as JSON. Keys are the disproportionality fields.
  disproportionalityMetrics: {
    prr?: number | null;
    prrCiLow?: number | null;
    prrCiHigh?: number | null;
    ror?: number | null;
    rorCiLow?: number | null;
    rorCiHigh?: number | null;
    chiSquare?: number | null;
    ic?: number | null;
    ic025?: number | null;
    icCiLow?: number | null;
    icCiHigh?: number | null;
  } | null;
  strengthOfEvidence: Record<string, unknown> | null;
  previousAwareness: Record<string, unknown> | null;
  clinicalRelevance: Record<string, unknown> | null;
  actions: SignalAction[];
}

type TabKey = 'summary' | 'metrics' | 'validation' | 'clock' | 'actions' | 'audit';

export function SignalDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canAct = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const [tab, setTab] = useState<TabKey>('summary');

  const signal = useQuery({
    queryKey: ['signal', id],
    queryFn: async () => {
      try {
        return (await api.get(`/api/signals/${id}`)).data as SignalDetail;
      } catch {
        return null;
      }
    },
  });

  const [esignOpen, setEsignOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const callAction = useMutation({
    mutationFn: async (action: string) => {
      // TODO(signals): expose distinct endpoints for each action in backend.
      return api.post(`/api/signals/${id}/${action}`, {});
    },
    onSuccess: (_, action) => {
      toast.success('Action recorded', action);
      qc.invalidateQueries({ queryKey: ['signal', id] });
    },
    onError: (err) => toast.error('Action failed', extractApiError(err).message),
  });

  // NOTE: Backend /api/signals/:id/close expects { rationale, evidence? }
  // rather than an esignature body. We pack the collected reason + intent
  // into rationale and include the password in evidence so the captured
  // signature is not lost. Backend may be extended later to validate the
  // password explicitly for full 21 CFR Part 11 equivalence here.
  const closeWithSig = useMutation({
    mutationFn: async (sig: ESignaturePayload) =>
      api.post(`/api/signals/${id}/close`, {
        rationale: `${sig.reason}: ${sig.intent}`,
        evidence: {
          esignature: { reason: sig.reason, intent: sig.intent },
        },
        // Forward esignature alongside so a future backend revision that
        // gates closure behind Part 11 re-auth can validate without a
        // frontend change.
        esignature: { password: sig.password, reason: `${sig.reason}: ${sig.intent}` },
      }),
    onSuccess: () => {
      toast.success('Signal closed', 'E-signature recorded');
      setEsignOpen(false);
      qc.invalidateQueries({ queryKey: ['signal', id] });
    },
    onError: (err) => toast.error('Close failed', extractApiError(err).message),
  });

  if (signal.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!signal.data) return <NoSignal />;

  const s = signal.data;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/signals">
            <ArrowLeft className="mr-2 h-4 w-4" /> Signals
          </Link>
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{s.activeSubstance ?? 'Unknown substance'}</h1>
            <p className="text-sm text-muted-foreground">
              {s.meddraPreferredTerm ?? 'No PT'} {s.meddraSoc ? `(${s.meddraSoc})` : ''} - {s.detectionSource ?? '-'}
            </p>
          </div>
          <Badge variant={s.validationStatus === 'CONFIRMED' ? 'success' : 'warning'}>
            {s.validationStatus ?? 'NEW'}
          </Badge>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {(
          [
            ['summary', 'Summary'],
            ['metrics', 'Disproportionality'],
            ['validation', 'Validation'],
            ['clock', 'Clock'],
            ['actions', 'Actions'],
            ['audit', 'Audit trail'],
          ] as [TabKey, string][]
        ).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === k
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Active substance" value={s.activeSubstance ?? '-'} />
            <Row label="MedDRA PT" value={s.meddraPreferredTerm ?? '-'} />
            <Row label="MedDRA SOC" value={s.meddraSoc ?? '-'} />
            <Row label="Source" value={s.detectionSource ?? '-'} />
            <Row label="Case count" value={s.caseCount ?? '-'} />
            <Row label="Priority" value={s.priorityLevel ?? '-'} />
            <Row label="Disposition" value={s.disposition ?? '-'} />
          </CardContent>
        </Card>
      )}

      {tab === 'metrics' && (
        <Card>
          <CardHeader>
            <CardTitle>Disproportionality metrics</CardTitle>
            <CardDescription>PRR, ROR, chi-square and IC with 95% CIs.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {s.disproportionalityMetrics ? (
              <>
                <Row
                  label="PRR"
                  value={fmtCi(
                    s.disproportionalityMetrics.prr ?? null,
                    s.disproportionalityMetrics.prrCiLow ?? null,
                    s.disproportionalityMetrics.prrCiHigh ?? null,
                  )}
                />
                <Row
                  label="ROR"
                  value={fmtCi(
                    s.disproportionalityMetrics.ror ?? null,
                    s.disproportionalityMetrics.rorCiLow ?? null,
                    s.disproportionalityMetrics.rorCiHigh ?? null,
                  )}
                />
                <Row label="Chi-square" value={fmtNum(s.disproportionalityMetrics.chiSquare ?? null)} />
                <Row
                  label="IC"
                  value={fmtCi(
                    s.disproportionalityMetrics.ic ?? null,
                    s.disproportionalityMetrics.icCiLow ?? s.disproportionalityMetrics.ic025 ?? null,
                    s.disproportionalityMetrics.icCiHigh ?? null,
                  )}
                />
              </>
            ) : (
              <p className="text-muted-foreground">No metrics computed yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'validation' && (
        <Card>
          <CardHeader>
            <CardTitle>5-element validation</CardTitle>
            <CardDescription>
              Strength, consistency, specificity, temporal relationship, biological plausibility.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {s.strengthOfEvidence || s.previousAwareness || s.clinicalRelevance ? (
              <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(
                  {
                    strengthOfEvidence: s.strengthOfEvidence,
                    previousAwareness: s.previousAwareness,
                    clinicalRelevance: s.clinicalRelevance,
                  },
                  null,
                  2,
                )}
              </pre>
            ) : (
              <p className="text-muted-foreground">Validation not yet run.</p>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'clock' && (
        <Card>
          <CardHeader>
            <CardTitle>Clock status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Notification type" value={s.notificationType ?? '-'} />
            <Row label="Day zero" value={s.dayZero ? formatDate(s.dayZero) : '-'} />
            <Row label="Due" value={s.notificationClockDue ? formatDate(s.notificationClockDue) : '-'} />
            <Row
              label="Sent"
              value={s.notificationSentAt ? formatDate(s.notificationSentAt) : 'Not yet sent'}
            />
          </CardContent>
        </Card>
      )}

      {tab === 'actions' && (
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
            <CardDescription>
              All actions are audit-logged. Close requires e-signature.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={!canAct || (callAction.isPending && pendingAction === 'validate')}
                onClick={() => {
                  setPendingAction('validate');
                  callAction.mutate('validate');
                }}
              >
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Validate
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!canAct || (callAction.isPending && pendingAction === 'disposition')}
                onClick={() => {
                  setPendingAction('disposition');
                  callAction.mutate('disposition');
                }}
              >
                Set disposition
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!canAct || (callAction.isPending && pendingAction === 'notify')}
                onClick={() => {
                  setPendingAction('notify');
                  callAction.mutate('notify');
                }}
              >
                <Bell className="mr-1.5 h-3.5 w-3.5" /> Notify
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!canAct || (callAction.isPending && pendingAction === 'prac-update')}
                onClick={() => {
                  setPendingAction('prac-update');
                  callAction.mutate('prac-update');
                }}
              >
                PRAC update
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={!canAct}
                onClick={() => setEsignOpen(true)}
              >
                <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Close (e-sign)
              </Button>
            </div>
            {!canAct && (
              <p className="text-xs text-muted-foreground">
                Requires REVIEWER role or higher.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle>Audit trail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            {!s.actions || s.actions.length === 0 ? (
              <p className="text-muted-foreground">No actions recorded yet.</p>
            ) : (
              s.actions.map((a) => (
                <div
                  key={a.id}
                  className="flex justify-between border-b py-1.5 last:border-0"
                >
                  <span>
                    {a.actionType}
                    {a.rationale ? ` - ${a.rationale}` : ''}
                  </span>
                  <span className="text-muted-foreground">{formatDate(a.performedAt)}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      <ESignatureModal
        open={esignOpen}
        onOpenChange={setEsignOpen}
        title="Close signal"
        description={`Closing signal for ${s.activeSubstance ?? 'unknown substance'} / ${s.meddraPreferredTerm ?? 'no PT'}. Closure is final and auditable.`}
        reasonPresets={[
          { value: 'closure', label: 'Closure' },
          { value: 'no-action', label: 'No action required' },
          { value: 'other', label: 'Other' },
        ]}
        submitLabel="Sign and close"
        isSubmitting={closeWithSig.isPending}
        onConfirm={async (sig) => {
          await closeWithSig.mutateAsync(sig);
        }}
      />
    </div>
  );
}

function NoSignal() {
  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/signals">
          <ArrowLeft className="mr-2 h-4 w-4" /> Signals
        </Link>
      </Button>
      <Card>
        <CardContent className="p-10 text-center text-sm text-muted-foreground">
          Signal not found or backend endpoint not yet available.
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b pb-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function fmtNum(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  return n.toFixed(2);
}

function fmtCi(val: number | null, lo: number | null, hi: number | null): string {
  if (val == null) return '-';
  const base = val.toFixed(2);
  if (lo != null && hi != null) return `${base} (95% CI ${lo.toFixed(2)}-${hi.toFixed(2)})`;
  return base;
}
